/**
 * Les ambiances du bandeau héros — une photo par onglet, DA 2027 (lot 3,
 * 2026-10-27).
 *
 * ## Ce qui a changé avec la DA 2027
 *
 * Le texte ne se pose plus sur la photo : le héros est un aplat rose, et la
 * photo vient dessous, en pleine largeur. Les voiles et les mesures de
 * contraste du verre sombre n'ont donc plus d'objet — ils sont retirés, pas
 * recopiés.
 *
 * ## La règle de cadrage
 *
 * Baptiste, le 2026-08-26 : « on doit toujours visualiser l'eau sur les images ».
 * Et le 2026-10-27, sur les maquettes : « descends un peu les images que l'on
 * puisse voir un peu plus de ligne d'horizon ». Le cadre est plus haut qu'en V4
 * (`clamp(260px, 28vw, 52vh)`) et centré sur la hauteur de l'image : **la crête
 * et l'eau dans le même cadre**.
 *
 * Références : `../oasis-2027-maquettes/` et ses captures `hero-*.png`.
 *
 * ⚠️ `zoom` agrandit l'image à 160 % calée à gauche — seul moyen de sortir du
 * cadre un défaut de la photo (Mon parcours). `flou` est facultatif : une photo
 * sans aperçu flou s'affiche sans transition.
 */
export type Ambiance = keyof typeof AMBIANCES

export type Reglage = {
  /** Le fichier, dans `public/brand/covers/`. */
  fichier: string
  /** `object-position`. */
  cadrage: string
  /** Agrandir à 160 %, calé à gauche (voir l'en-tête). */
  zoom?: boolean
  /** La photo en 20 px de large, le temps qu'elle arrive. */
  flou?: string
}

export const AMBIANCES = {
  /** La cascade et son bassin, entre les palmes. Le panorama entier de l'accueil. */
  accueil: {
    fichier: '/brand/covers/hero-accueil.jpg',
    cadrage: 'center 60%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABtAAEBAQEAAAAAAAAAAAAAAAAFBAMHAQEBAQEBAAAAAAAAAAAAAAAEBQMCBhAAAQQCAgMBAAAAAAAAAAAAAQBxAhEDQTIhUYHBMxEAAQMCBgMBAQAAAAAAAAAAAQIAEQMxcWESQSFRMoGhcjP/wAARCAAIABQDARIAAhIAAxIA/9oADAMBAAIRAxEAPwDm2MT1VDzxZWY/xyuFO1aTwSDldmV5h+iQFHqB3Ztp/wA14tETAibjZv1Sm0W+KipVcwQoRE8AfWynY/l9AAAyJM5vU2PtimRJNWO9LaG3UuT3i8S4smTE32a0b4v/2Q==',
  },
  /** Les rochers au bord de l'eau. ⚠️ Le tiers droit porte un bloc sombre et flou : on zoome à 160 % sur la gauche (décision de Baptiste, 2026-10-27). */
  parcours: {
    fichier: '/brand/covers/hero-parcours.jpg',
    cadrage: '0 80%',
    zoom: true,
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xAB3AAEBAQAAAAAAAAAAAAAAAAAGAgUBAAMBAQEAAAAAAAAAAAAAAAYFBAIAAxAAAQIFAwUBAAAAAAAAAAAAARECEgAEAyFxMjFhoXKxwlERAAEDAgMGBwEAAAAAAAAAAAEAAhEhA3FyMUEyM8ESBJGhgXNhsVEi/8AAEQgACAAUAwESAAISAAMSAP/aAAwDAQACEQMRAD8AK0bnuBMSAYRM4TmYotr9XfMrbt42yIBk7ZxS/udR6c0Z2Qbjf6qJIAitPkKrtd053JNcq3MDTBuQZXBP7jicyq4Z5W/clIunpJLXCPA4FadwDl5JS62BEHUxhiqTvj3B9ofcr77nFHw9AE7FTJt+52pnutxU7dBghlz4JAmn7Ty2Lzu8R+Yr/9k=',
  },
  /** Le bassin entre les rochers et les palmiers, la crête au fond. */
  agenda: {
    fichier: '/brand/covers/hero-agenda.jpg',
    cadrage: 'center 50%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABvAAEBAQEAAAAAAAAAAAAAAAAGBQQHAQEBAQEBAAAAAAAAAAAAAAAFBAMCBhAAAQMDAgcBAAAAAAAAAAAAAQAEEQIxAzJxUWEisSES8UERAAIBAgcBAQEAAAAAAAAAAAECABEDQWExIiFxEgQygf/AABEIAAYAFAMBEgACEgADEgD/2gAMAwEAAhEDEQA/AOYt3Rx+ojyD0m0Sf1H8eqncd1AUPqoNDLcY1auqq+WBPNMuTCF1HYjxy9zaTUY35Rwsjzm53XTJcA3PXqKXPz/Y3cZFO1aSC7hMlTiqo/FHXnwgEqmh+h68aZ0hM//Z',
  },
  /** Le canyon et sa rivière. */
  elearning: {
    fichier: '/brand/covers/hero-elearning.jpg',
    cadrage: 'center 50%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABzAAEBAQEAAAAAAAAAAAAAAAACAwQGAQEBAQEBAAAAAAAAAAAAAAAEBQIGAxAAAQMCBQUBAQAAAAAAAAAAAQIAERIDM7KCITHBcTLw0UFCEQABAwMEAQUBAAAAAAAAAAABAAIRQbGBkTIxA9EiIeFxQhL/wAARCAAIABQDARIAAhIAAxIA/9oADAMBAAIRAxEAPwDjbFRB3pFMD839l2t4adOVTlvd/BBHM0Xg/frcLsWAuBFEnr2jFiiu6tKf58okDvuXhuYerq65d2ekyQCCdKJT+G5U6AJ4MGFgfr7F0JQOazM8OX05i4/uatysjxZLgCjsJvzcr//Z',
  },
  /** Le lac et la palmeraie, la montagne qui s'y reflète. */
  replays: {
    fichier: '/brand/covers/hero-replays.jpg',
    cadrage: 'center 45%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABxAAEBAQEAAAAAAAAAAAAAAAAEBQYHAQEBAQEBAAAAAAAAAAAAAAAEBQYBAxAAAQMCAgsBAAAAAAAAAAAAAgABAwQzcSKBcpGhEtFCIRHBQVERAAIBAwQDAQAAAAAAAAAAAAABAhEDQTEhsRKRMqFR/8AAEQgACAAUAwESAAISAAMSAP/aAAwDAQACEQMRAD8A50NRJ3zntLmpofcPa9O1yuzfgTHVmmi1lLgGjWwzz+MpG7avFvdMobb4uuudym0U2PiVV0b1J8cgnqZuqQwf8YUequaGWblcvVzwUrnsVkoU/PoE/9k=',
  },
  /** Les rochers violets et les palmes orangées au bord de l'eau. */
  supports: {
    fichier: '/brand/covers/hero-supports.jpg',
    cadrage: 'center 50%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABvAAEBAQAAAAAAAAAAAAAAAAAFAgcBAAMBAQAAAAAAAAAAAAAAAAEEBQMCEAABAwQBBQEAAAAAAAAAAAABAAIRcRIxA8GhUUEz8CERAAIBAgYBBQEAAAAAAAAAAAIBAAMxwUFRMyERcZFyEkLwYf/AABEIAAYAFAMBEgACEgADEgD/2gAMAwEAAhEDEQA/AMvbBBz+AqNeH0PC5ZtactRc7D7ljHRSfevT5hp/bw8Iy2bHOJJDfE9KKm+nZXhbVKrEhFZyfW3Q8Yx8BSEnf45TdbdSDO3XHEfURXdWk3/IFZSOVXt2/eknZuf/2Q==',
  },
  /**
   * La main qui signe, sur fond rose et violet — photo fournie par Baptiste le
   * 2026-10-27 pour « Mes documents » et la signature d'émargement. ⚠️ Exception
   * voulue à « l'eau toujours visible » : ces deux écrans parlent de signature.
   * Cadrée vers le bas pour garder la main, le stylo et la feuille entiers.
   */
  documents: {
    fichier: '/brand/covers/hero-signature.webp',
    cadrage: 'center 75%',
  },
  /**
   * Le lexique (2026-11-04) : les rochers ronds dans l'eau, la crête en
   * flammes. C'était la photo de « Mes documents » avant qu'il ne passe à la
   * signature ; Baptiste ne voulait pas la même image que l'e-learning.
   */
  lexique: {
    fichier: '/brand/covers/hero-documents.jpg',
    cadrage: 'center 60%',
    flou:
      'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD/4QBMRXhpZgAATU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAFKADAAQAAAABAAAABwAAAAD/7QA4UGhvdG9zaG9wIDMuMAA4QklNBAQAAAAAAAA4QklNBCUAAAAAABDUHYzZjwCyBOmACZjs+EJ+/8AAEQgABwAUAwEiAAIRAQMRAf/EAB8AAAEFAQEBAQEBAAAAAAAAAAABAgMEBQYHCAkKC//EALUQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+v/EAB8BAAMBAQEBAQEBAQEAAAAAAAABAgMEBQYHCAkKC//EALURAAIBAgQEAwQHBQQEAAECdwABAgMRBAUhMQYSQVEHYXETIjKBCBRCkaGxwQkjM1LwFWJy0QoWJDThJfEXGBkaJicoKSo1Njc4OTpDREVGR0hJSlNUVVZXWFlaY2RlZmdoaWpzdHV2d3h5eoKDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uLj5OXm5+jp6vLz9PX29/j5+v/bAEMABAQEBAQEBgQEBgkGBgYJDAkJCQkMDwwMDAwMDxIPDw8PDw8SEhISEhISEhUVFRUVFRkZGRkZHBwcHBwcHBwcHP/bAEMBBAUFBwcHDAcHDB0UEBQdHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHf/dAAQAAv/aAAwDAQACEQMRAD8A+X7G9IvIltWaF9wBdTyPXA4yPY16Enje80uxWSXFwXQFXK7SGYZAIB5/CvLdM/5CMf8Av1r6r/yCYP8Atl/6BXBTxleniIU4Tdmrv8T9lw1p4OrWktVe34GrqfjzXr64E0V89mAir5cWQuQOT+JrP/4TDxF/0GJ/1rkpPvmmV9JNpSat+Mv8zwHiJXZ//9k=',
  },
  /** La signature d'émargement : la même photo que « Mes documents ». */
  signature: {
    fichier: '/brand/covers/hero-signature.webp',
    cadrage: 'center 75%',
  },
  /** La même que « replays » : « Mon équipe » n'a pas reçu de photo, et un compte entreprise ne voit pas les deux onglets ensemble. */
  equipe: {
    fichier: '/brand/covers/hero-replays.jpg',
    cadrage: 'center 45%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABxAAEBAQEAAAAAAAAAAAAAAAAEBQYHAQEBAQEBAAAAAAAAAAAAAAAEBQYBAxAAAQMCAgsBAAAAAAAAAAAAAgABAwQzcSKBcpGhEtFCIRHBQVERAAIBAwQDAQAAAAAAAAAAAAABAhEDQTEhsRKRMqFR/8AAEQgACAAUAwESAAISAAMSAP/aAAwDAQACEQMRAD8A50NRJ3zntLmpofcPa9O1yuzfgTHVmmi1lLgGjWwzz+MpG7avFvdMobb4uuudym0U2PiVV0b1J8cgnqZuqQwf8YUequaGWblcvVzwUrnsVkoU/PoE/9k=',
  },
  /** La même que « agenda » : ces rendez-vous SONT des entrées d'agenda — mêmes lignes de `seances`, mêmes séries iCal. */
  collectifs: {
    fichier: '/brand/covers/hero-agenda.jpg',
    cadrage: 'center 50%',
    flou:
      'data:image/jpeg;base64,/9j//gAQTGF2YzYwLjMxLjEwMgD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABvAAEBAQEAAAAAAAAAAAAAAAAGBQQHAQEBAQEBAAAAAAAAAAAAAAAFBAMCBhAAAQMDAgcBAAAAAAAAAAAAAQAEEQIxAzJxUWEisSES8UERAAIBAgcBAQEAAAAAAAAAAAECABEDQWExIiFxEgQygf/AABEIAAYAFAMBEgACEgADEgD/2gAMAwEAAhEDEQA/AOYt3Rx+ojyD0m0Sf1H8eqncd1AUPqoNDLcY1auqq+WBPNMuTCF1HYjxy9zaTUY35Rwsjzm53XTJcA3PXqKXPz/Y3cZFO1aSC7hMlTiqo/FHXnwgEqmh+h68aZ0hM//Z',
  },
} as const satisfies Record<string, Reglage>
